// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { classNames } from '@shared/ui-kit/utils/classnames';
import { createColumnHelper } from '@tanstack/react-table';
import { AssetBrand } from '@shared/ui-kit/components/popups';
import { type InterfacePositionsAnalysisData } from '@advisor-app/features/positions';
import { type ColumnMethods } from '@advisor-app/features/trades';
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { CellData, WatchlistToggleButton } from '@shared/ui-kit/components';
import { DeleteButton } from '@shared/ui-kit/components/delete-button';

const columnHelper = createColumnHelper<InterfacePositionsAnalysisData>();

export const renderColumnsAnalysisExtended = (columnMethods: ColumnMethods) => {
  const { handleDeleteTrade, assetType, hasOnlyOneData } = columnMethods;

  const isStock = assetType === 'stock';

  const columns = [
    columnHelper.accessor('name', {
      header: 'NAME',
      minSize: 300,
      enableHiding: false,
      cell: ({ row }) => (
        <AssetBrand
          ticker={row.original.ticker}
          assetType={row.original.asset_type}
          label={row.original.name}
          url={row.original.url_morningstar}
          logoUrl={row.original.url_logo}
          watchlistButton={
            <WatchlistToggleButton
              ticker={row.original.ticker}
              assetType={row.original.asset_type!}
            />
          }
        />
      ),
    }),

    columnHelper.accessor('ticker', {
      cell: ({ getValue }) => getValue(),
      header: 'TICKER',
      enableHiding: false,
    }),

    columnHelper.accessor('isin', {
      header: 'ISIN',
      cell: ({ getValue }) => getValue() ?? '-',
    }),

    columnHelper.accessor('weight', {
      header: 'WEIGHT',
      meta: { alignment: 'end' },
      cell: ({ getValue }) => (
        <span className="!money-display">{getValue() + ' %'}</span>
      ),
    }),

    columnHelper.accessor('quantity', {
      header: 'QUANTITY',
      meta: { alignment: 'end' },
      cell: ({ getValue }) => <span className="!money-display">{getValue()}</span>,
    }),

    columnHelper.accessor('price', {
      header: 'PRICE',
      meta: { alignment: 'end' },
      minSize: 100,
      cell: ({ getValue }) => <CellData value={getValue()} format="currency" />,
    }),

    columnHelper.accessor('outstanding_balance', {
      header: 'INVESTMENT',
      meta: { alignment: 'end' },
      cell: ({ getValue }) => (
        <span className="!money-display">{formatToMoney(getValue())}</span>
      ),
    }),

    columnHelper.accessor('unrealized_capital_gains', {
      header: 'GAIN / LOSS',
      sortingFn: 'gainLoss',
      minSize: 200,
      meta: { alignment: 'end' },
      cell: ({ getValue }) => {
        const [value, percentage] = getValue();
        return (
          <div className={value >= 0 ? 'profit' : 'loss'}>
            {formatToMoney(value)} ({percentage}%)
          </div>
        );
      },
    }),

    columnHelper.accessor('ytd_return', {
      header: 'YTD RETURN',
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('y1_return', {
      header: '1Y RETURN',
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('y3_return', {
      header: '3Y RETURN',
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('y5_return', {
      header: '5Y RETURN',
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('y10_return', {
      header: '10Y RETURN',
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('y3_vol', {
      header: '3Y VOL',
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => (
        <CellData
          value={getValue()}
          format="percentage"
          decimalPlaces={1}
          emptyValue={-1}
        />
      ),
    }),

    columnHelper.accessor('max_draw_down', {
      header: 'MAX DRAWDOWN',
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => (
        <CellData
          value={getValue()}
          format="percentage"
          decimalPlaces={1}
          emptyValue={-1}
        />
      ),
    }),

    columnHelper.accessor('inception_return', {
      header: 'Inception Return',
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('m1_return', {
      header: 'M1 Return',
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('m3_return', {
      header: '3M Return',
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('m6_return', {
      header: '6M Return',
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('price_as_of', {
      header: 'Price as of',
      meta: {
        initiallyHidden: true,
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="date" />,
    }),

    columnHelper.display({
      id: 'options',
      meta: { showColumnVisibilitySelector: true },
      cell: ({ row }) => (
        <DeleteButton
          className={classNames({ '!hidden': hasOnlyOneData })}
          data-testid="delete-trade"
          onClick={() => handleDeleteTrade?.(row.original.ticker)}
        />
      ),
    }),
  ];

  return columns;
};
